import { Card, H1, H3 } from '@/components'
import { MonthPicker, UserSelect } from '@/components_deprecated'
import { Field } from '@/modules/companies'
import { format } from '@/utils'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, parseISO, startOfMonth } from 'date-fns'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import { OUT_OF_SCOPE_DRIVING_EVENTS } from '../queries'

export const OutOfScopeDriving: React.FC = () => {
  const translations = useTranslate({
    title: 'activities.driving-in-out',
    month: 'common.month',
    user: 'common.user',
    period: 'common.period',
    vehicle: 'common.vehicle',
    duration: 'common.duration',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.activities.view.driveractivity,
    true
  )
  const form = useForm<{
    user: string | null
    datetimeStart: Date
    datetimeEnd: Date
  }>({
    values: {
      user: null,
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'out-of-scope-driving-filters',
        transformValues: {
          user: val => {
            return val && canSeeOthers ? String(val) : null
          },
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { data } = useQuery(OUT_OF_SCOPE_DRIVING_EVENTS, {
    variables: {
      userId: form.formValues.user ?? (canSeeOthers ? null : me.id),
      datetimeStart: formatISO(form.formValues.datetimeStart),
      datetimeEnd: formatISO(form.formValues.datetimeEnd),
    },
  })

  const events = (data?.outOfScopeDrivingEvents ?? []) as {
    id: string
    start: string
    end: string
    duration?: string | null
    vehicleVin?: string | null
    vehicleRegistrationNumber?: string | null
    user: {
      fullName: string
    }
  }[]

  return (
    <div className="relative box-border px-4 py-4 md:px-[52px] md:py-8 md:pb-[52px]">
      <H1>{translations.title}</H1>
      <Card
        className="mb-4 p-0"
        contentClassName="grid grid-cols-1 md:grid-cols-[1fr_auto]"
      >
        <section className="grid grid-cols-1 gap-4 p-4 md:grid-cols-[1fr_auto]">
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserSelect
                value={form.formValues.user}
                grayed
                width="100%"
                minWidth={isMobileOnly ? '0' : '260px'}
                height="3.875rem"
                onChange={user => form.updateForm({ user: user ?? null })}
                filterSalaryResponsible={false}
                filterLeaderGroup
                nullable
              />
            </Field>
          )}
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                form.updateForm({
                  datetimeStart: startOfMonth(date),
                  datetimeEnd: endOfMonth(date),
                })
              }}
            />
          </Field>
        </section>
      </Card>

      <div className="flex flex-col gap-2">
        {events.map(event => {
          const startDate = parseISO(event.start)
          const endDate = parseISO(event.end)
          return (
            <Card
              key={event.id}
              className="border border-[#E1E1E1] bg-white px-5 py-4"
            >
              <div>
                <H3>{event.user.fullName}</H3>
                <div>
                  <strong>{translations.period}: </strong>
                  {format(startDate, 'dd.MM.yyyy HH:mm')} -{' '}
                  {format(endDate, 'dd.MM.yyyy HH:mm')}
                </div>
                <div>
                  <strong>{translations.vehicle}: </strong>
                  {event.vehicleRegistrationNumber || event.vehicleVin || 'N/A'}
                </div>
                <div>
                  <strong>{translations.duration}: </strong>
                  {event.duration}
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
